import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { ChevronDownIcon } from "lucide-react";

import { Row } from "@/components/Row";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { IndustryType, SkillType } from "@/types";

const badgeGap = 8;
const badgeHeight = 26;
const maxInitialRows = 2;
const maxHeight =
  badgeHeight * maxInitialRows + badgeGap * (maxInitialRows - 1);

export const SheetCollapsibleIndustryOrSkillList = ({
  industriesOrSkills,
}: {
  industriesOrSkills: IndustryType[] | SkillType[];
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Check if content exceeds two lines on mount and window resize.
  useEffect(() => {
    const checkHeight = () => {
      if (containerRef.current) {
        setHasOverflow(containerRef.current.scrollHeight > maxHeight);
      }
    };

    checkHeight();

    window.addEventListener("resize", checkHeight);
    return () => {
      window.removeEventListener("resize", checkHeight);
    };
  }, [industriesOrSkills]); // check height on change of industriesOrSkills to ensure it adjusts upon switching between candidates

  const handleExpandClick = () => {
    setIsExpanded(true);
  };

  return (
    <>
      <div
        className={clsx(
          // Gap needs to be kept in sync with `badgeGap` above.
          "flex flex-wrap gap-2",
          hasOverflow && "relative overflow-hidden",
        )}
        onClick={!isExpanded && hasOverflow ? handleExpandClick : undefined}
        ref={containerRef}
        // Have to do this because Tailwind doesn't support dynamic height.
        style={{
          height: !isExpanded && hasOverflow ? maxHeight : undefined,
        }}
      >
        {industriesOrSkills.map(
          (industryOrSkill: IndustryType | SkillType, i) => (
            <Badge className="bg-slate-50" key={i} variant="outline">
              {industryOrSkill.name}
            </Badge>
          ),
        )}
        {!isExpanded && hasOverflow && (
          <div
            className="absolute bottom-0 left-0 right-0 bg-gradient-to-b from-transparent to-white dark:to-[#212327]"
            style={{
              height: maxHeight,
            }}
          />
        )}
      </div>
      {!isExpanded && hasOverflow && (
        <Row className="justify-center">
          <Button
            className="h-auto w-auto p-0 text-primary-900 hover:bg-transparent hover:text-primary-900"
            onClick={handleExpandClick}
            variant="ghost"
          >
            Expand <ChevronDownIcon className="h-4 w-4" />
          </Button>
        </Row>
      )}
    </>
  );
};
